<template>
  <section id="leaderboard"
    class="relative w-full overflow-hidden p-2 py-24 pb-36 text-center sm:px-4 md:px-8 lg:px-16 xl:px-24">
    <!-- Refactored: single dynamic logo for roobet/packdraw -->
    <img v-if="['roobet', 'packdraw'].includes(currentLeaderboard)" v-motion-pop-visible
      class="z-50 mx-auto h-auto w-100" :src="leaderboardLogo" alt="leaderboard logo" />
    <img class="absolute -top-20 z-0 w-full overflow-hidden bg-cover" src="@/assets/leaderboard-stars.png"
      alt="small stars" />

    <!-- Desktop leaderboard switch -->
    <div v-motion-pop-visible class="flex items-center justify-center mt-10 md:mt-16">
      <div
        class="relative z-30 flex items-center justify-between rounded-full w-[360px] sm:w-[460px] lg:w-[560px] h-[90px] lg:h-[100px] px-6 border border-[rgba(255,255,255,0.2)] bg-gradient-to-bl from-[rgba(4,2,46,0.3)] to-[rgba(4,2,46,0.5)] backdrop-blur-md overflow-hidden gap-6">
        <!-- Sliding Background -->
        <div
          class="absolute top-1 bottom-1 left-1 h-[calc(100%-8px)] rounded-full transition-all duration-300 ease-in-out pointer-events-none bg-gradient-to-bl from-[rgba(255,255,255,0.15)] to-[rgba(221,221,221,0.1)]"
          :style="{
            width: `calc(50% - 8px)`,
            transform:
              currentLeaderboard === 'roobet'
                ? 'translateX(0)'
                : 'translateX(calc(100% + 8px))',
          }"></div>

        <!-- Roobet Logo Button -->
        <div class="z-30 relative flex items-center justify-center h-full cursor-pointer px-4 flex-1"
          @click="toggleLeaderboard('roobet')">
          <img src="@/assets/roobet-logo.avif" alt="Roobet"
            class="max-h-[40px] md:max-h-[50px] w-auto object-contain" />
        </div>

        <!-- Packdraw Logo Button -->
        <div class="z-30 relative flex items-center justify-center h-full cursor-pointer px-4 flex-1"
          @click="toggleLeaderboard('packdraw')">
          <img src="@/assets/packdraw-logo.avif" alt="Packdraw"
            class="max-h-[40px] md:max-h-[50px] w-auto object-contain" />
        </div>
      </div>
    </div>

    <!-- Current vs Previous Leaderboard Sub-tab Switcher -->
    <div v-if="showPreviousLeaderboard" v-motion-pop-visible class="flex items-center justify-center mt-6 sm:mt-8 z-30 relative">
      <div class="relative z-30 flex items-center justify-center rounded-full p-2 sm:p-2.5 border border-[rgba(255,255,255,0.2)] bg-gradient-to-bl from-[rgba(4,2,46,0.4)] to-[rgba(4,2,46,0.7)] backdrop-blur-md gap-2 sm:gap-3">
        <button
          @click="selectedTab = 'current'"
          :class="[
            'px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-inter font-bold text-xs sm:text-base transition-all duration-300 cursor-pointer',
            selectedTab === 'current'
              ? 'bg-gradient-to-r from-[#FFC125] to-[#F59E0B] text-black shadow-lg scale-105'
              : 'text-white/80 hover:text-white hover:bg-white/10'
          ]"
        >
          Current Leaderboard
        </button>
        <button
          @click="selectedTab = 'previous'"
          :class="[
            'px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-inter font-bold text-xs sm:text-base transition-all duration-300 cursor-pointer',
            selectedTab === 'previous'
              ? 'bg-gradient-to-r from-[#FFC125] to-[#F59E0B] text-black shadow-lg scale-105'
              : 'text-white/80 hover:text-white hover:bg-white/10'
          ]"
        >
          Previous Leaderboard
        </button>
      </div>
    </div>

    <!-- leaderboards timer -->
    <div v-if="!isLeaderboardPaused && selectedTab === 'current'" class="flex flex-col items-center justify-center my-8 lg:mt-12 lg:mb-5">
      <div class="bg-transparent">
        <div v-motion-slide-visible-bottom class="bg-transparent" style="font-family: Oxanium">
          <!-- Timer -->
          <div v-if="currentLeaderboard && timer !== 'Leaderboard ended'" class="flex flex-row gap-x-2">
            <!-- Days Timer -->
            <div class="relative shadow-[0_0_50px_#001A94] rounded-[30px] h-[84px] w-[84px] md:h-[100px] md:w-[100px]">
              <img src="@/assets/countdown.png" alt="Timer Background"
                class="absolute inset-0 object-cover h-[84px] w-[84px] md:w-[100px] md:h-[100px]" />
              <div class="absolute inset-0 flex flex-col items-center justify-center">
                <h4 class="text-white text-[36px] leading-[46px] font-bold text-center">
                  {{ timer.split(" ")[0].slice(0, -1) }}
                </h4>
                <h6 class="text-sm leading-6 font-bold text-center text-white">DAYS</h6>
              </div>
            </div>

            <!-- Hours Timer -->
            <div class="relative shadow-[0_0_50px_#001A94] rounded-[30px] h-[84px] w-[84px] md:h-[100px] md:w-[100px]">
              <img src="@/assets/countdown.png" alt="Timer Background"
                class="absolute inset-0 object-cover h-[84px] w-[84px] md:h-[100px] md:w-[100px]" />
              <div class="absolute inset-0 flex flex-col items-center justify-center">
                <h4 class="text-white text-[36px] leading-[46px] font-bold text-center">
                  {{ timer.split(" ")[1].slice(0, -1) }}
                </h4>
                <h6 class="text-sm leading-6 font-bold text-center text-white">HRS</h6>
              </div>
            </div>

            <!-- Minutes Timer -->
            <div class="relative shadow-[0_0_50px_#001A94] rounded-[30px] h-[84px] w-[84px] md:h-[100px] md:w-[100px]">
              <img src="@/assets/countdown.png" alt="Timer Background"
                class="absolute inset-0 object-cover h-[84px] w-[84px] md:h-[100px] md:w-[100px]" />
              <div class="absolute inset-0 flex flex-col items-center justify-center">
                <h4 class="text-white text-[36px] leading-[46px] font-bold text-center">
                  {{ timer.split(" ")[2].slice(0, -1) }}
                </h4>
                <h6 class="text-sm leading-6 font-bold text-center text-white">MIN</h6>
              </div>
            </div>

            <!-- Seconds Timer -->
            <div class="relative shadow-[0_0_50px_#001A94] rounded-[30px] h-[84px] w-[84px] md:h-[100px] md:w-[100px]">
              <img src="@/assets/countdown.png" alt="Timer Background"
                class="absolute inset-0 object-cover h-[84px] w-[84px] md:h-[100px] md:w-[100px]" />
              <div class="absolute inset-0 flex flex-col items-center justify-center">
                <h4 class="text-white text-[36px] leading-[46px] font-bold text-center">
                  {{ timer.split(" ")[3].slice(0, -1) }}
                </h4>
                <h6 class="text-sm leading-6 font-bold text-center text-white">SEC</h6>
              </div>
            </div>
          </div>

          <!-- Leaderboard Ended Message -->
          <div v-else class="text-white text-2xl md:text-[36px] md:leading-[48px] font-bold py-[11px]">
            Leaderboard Ended
          </div>
        </div>
      </div>
      <p v-if="startDate && endDate" class="text-white/80 font-inter text-sm md:text-base font-semibold mt-3">
        Cycle Period: {{ startDate }} – {{ endDate }}
      </p>
    </div>

    <!-- Previous Leaderboard Banner -->
    <div v-else-if="!isLeaderboardPaused && selectedTab === 'previous'" class="flex flex-col items-center justify-center my-8 lg:mt-12 lg:mb-5">
      <h2 class="text-2xl md:text-4xl font-extrabold text-[#FFC125] tracking-wider uppercase font-inter">
        PREVIOUS LEADERBOARD RESULTS
      </h2>
      <p class="text-white/80 font-inter text-sm md:text-base font-semibold mt-2">
        Completed Cycle Results
      </p>
    </div>

    <!-- medium cloud right overlay -->
    <img class="absolute top-12 -right-24 z-10 h-[4em] overflow-hidden sm:h-[6em] md:h-[9.5em]"
      src="@/assets/medium-cloud-1.png" alt="medium clouds" />
    <img class="absolute top-80 -left-20 z-10 h-[4em] overflow-hidden sm:top-96 sm:h-[6em] md:h-[9.5em]"
      src="@/assets/medium-cloud-2.png" alt="medium  clouds" />

    <!-- leaderboards top 3 -->
    <!-- Leaderboard loading message -->
    <div v-if="loading == true" class="mt-10 h-screen text-center text-xl font-bold text-white md:text-4xl">
      Loading Leaderboard...
    </div>

    <!-- leaderboard header top 3 -->
    <div v-if="loading == false" class="relative z-30 w-full">
      <div v-if="!isLeaderboardPaused"
        class="flex flex-row items-center justify-center gap-2 mt-20 sm:gap-10 md:gap-20 lg:mt-[150px] lg:gap-24">
        <!-- 2nd Place -->
        <div v-motion-pop-visible class="mt-80 flex flex-col items-center lg:mt-[88px]">
          <div
            class="z-50 w-[180px] rounded-[50px] border-[6px] border-[#FFC125]/80 bg-gradient-to-t from-[#FFFFFF15] to-[#D9D9D90B] sm:w-[200px] lg:w-[300px]"
            style="
              background: linear-gradient(to top, #ffffff15, #d9d9d90b);
              backdrop-filter: blur(10px);
              -webkit-backdrop-filter: blur(10px);
            ">
            <div class="relative flex flex-col items-center justify-center p-4">
              <img class="absolute top-0 -mt-[52px] -ml-10 h-[80px] w-auto translate-x-4 lg:-mt-[100px] lg:h-[150px]"
                :src="currentLeaderboard === 'raingg'
                    ? require('../assets/raingg-leaderboard-2nd.png')
                    : currentLeaderboard === 'packdraw'
                      ? require('../assets/packdraw-leaderboard-2nd.png')
                      : require('../assets/leaderboard-2nd.png')
                  " alt="leaderboard second" />

              <div class="mt-5 flex w-full flex-col items-center justify-center gap-2 sm:gap-4 lg:mt-8 lg:gap-6">
                <p class="z-50 font bold font-inter text-sm text-white lg:text-xl">
                  {{ activeTop3.length > 1 ? getMaskstring(activeTop3[1]?.username) : "—" }}
                </p>
                <div>
                  <p v-html="activeTop3.length > 1 ? getCurrencyFormatted(activeTop3[1]?.wagered) : '$0.00'"
                    class="z-50 font-inter text-lg font-bold text-white md:text-3xl lg:text-4xl"></p>
                  <p class="font-inter text-base uppercase text-white lg:text-lg">
                    WAGER
                  </p>
                </div>
                <div
                  class="relative my-1 w-full rounded-[10px] bg-gradient-to-r from-[#FFC125] to-[#FFC125]/20 py-1 lg:rounded-[20px] lg:py-5">
                  <p class="z-50 font-montserrat text-2xl font-bold text-white lg:text-3xl">
                    <span class="text-white">{{ getRewardValue(1) }}</span>
                    <img v-if="currentLeaderboard === 'raingg'" src="@/assets/raingg-coin.png" alt="raingg"
                      class="inline-block h-4 sm:h-7 w-4 sm:w-7 ml-1 align-[-2px]" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 1st Place -->
        <div v-motion-pop-visible class="absolute top-12 flex w-[150px] flex-col items-center lg:relative lg:-mt-40">
          <div
            class="z-50 w-[180px] rounded-[50px] border-[6px] border-[#FFC125]/80 bg-gradient-to-t from-[#FFFFFF15] to-[#D9D9D90B] sm:w-[200px] lg:w-[300px]"
            style="
              background: linear-gradient(to top, #ffffff15, #d9d9d90b);
              backdrop-filter: blur(10px);
              -webkit-backdrop-filter: blur(10px);
            ">
            <div class="relative flex flex-col items-center justify-center p-4">
              <img class="absolute top-0 -mt-[52px] -ml-10 h-[80px] w-auto translate-x-4 lg:-mt-[100px] lg:h-[150px]"
                :src="currentLeaderboard === 'raingg'
                    ? require('../assets/raingg-leaderboard-1st.png')
                    : currentLeaderboard === 'packdraw'
                      ? require('../assets/packdraw-leaderboard-1st.png')
                      : require('../assets/leaderboard-1st.png')
                  " alt="leaderboard first" />
              <div class="mt-5 flex w-full flex-col items-center justify-center gap-2 sm:gap-4 lg:mt-8 lg:gap-6">
                <p class="z-50 font bold font-inter text-sm text-white lg:text-xl">
                  {{ activeTop3.length > 0 ? getMaskstring(activeTop3[0]?.username) : "—" }}
                </p>
                <div>
                  <p v-html="activeTop3.length > 0 ? getCurrencyFormatted(activeTop3[0]?.wagered) : '$0.00'"
                    class="z-50 font-inter text-lg font-bold text-white md:text-3xl lg:text-4xl"></p>
                  <p class="font-inter text-base uppercase text-white lg:text-lg">
                    WAGER
                  </p>
                </div>
                <div
                  class="relative my-1 w-full rounded-[10px] bg-gradient-to-r from-[#FFC125] to-[#FFC125]/20 py-1 lg:rounded-[20px] lg:py-5">
                  <p class="z-50 font-montserrat text-2xl font-bold text-white lg:text-3xl">
                    <span class="text-white">{{ getRewardValue(0) }}</span>
                    <img v-if="currentLeaderboard === 'raingg'" src="@/assets/raingg-coin.png" alt="raingg"
                      class="inline-block h-4 sm:h-7 w-4 sm:w-7 ml-1 align-[-2px]" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 3rd Place -->
        <div v-motion-pop-visible class="mt-80 flex flex-col items-center lg:mt-[88px]">
          <div
            class="z-50 w-[180px] rounded-[50px] border-[6px] border-[#FFC125]/80 bg-gradient-to-t from-[#FFFFFF15] to-[#D9D9D90B] sm:w-[200px] lg:w-[300px]"
            style="
              background: linear-gradient(to top, #ffffff15, #d9d9d90b);
              backdrop-filter: blur(10px);
              -webkit-backdrop-filter: blur(10px);
            ">
            <div class="relative flex flex-col items-center justify-center p-4">
              <img class="absolute top-0 -mt-[52px] -ml-10 h-[80px] w-auto translate-x-4 lg:-mt-[100px] lg:h-[150px]"
                :src="currentLeaderboard === 'raingg'
                    ? require('../assets/raingg-leaderboard-3rd.png')
                    : currentLeaderboard === 'packdraw'
                      ? require('../assets/packdraw-leaderboard-3rd.png')
                      : require('../assets/leaderboard-3rd.png')
                  " alt="leaderboard third" />
              <div class="mt-5 flex w-full flex-col items-center justify-center gap-2 sm:gap-4 lg:mt-8 lg:gap-6">
                <p class="z-50 font bold font-inter text-sm text-white lg:text-xl">
                  {{ activeTop3.length > 2 ? getMaskstring(activeTop3[2]?.username) : "—" }}
                </p>
                <div>
                  <p v-html="activeTop3.length > 2 ? getCurrencyFormatted(activeTop3[2]?.wagered) : '$0.00'"
                    class="z-50 font-inter text-lg font-bold text-white md:text-3xl lg:text-4xl"></p>
                  <p class="font-inter text-base uppercase text-white lg:text-lg">
                    WAGER
                  </p>
                </div>
                <div
                  class="relative my-1 w-full rounded-[10px] bg-gradient-to-r from-[#FFC125] to-[#FFC125]/20 py-1 lg:rounded-[20px] lg:py-5">
                  <p class="z-50 font-montserrat text-2xl font-bold text-white lg:text-3xl">
                    <span class="text-white">{{ getRewardValue(2) }}</span>
                    <img v-if="currentLeaderboard === 'raingg'" src="@/assets/raingg-coin.png" alt="raingg"
                      class="inline-block h-4 sm:h-7 w-4 sm:w-7 ml-1 align-[-2px]" />
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- small cloud right overlay -->
    <img class="absolute top-1/2 right-0 z-10 h-[4em] overflow-hidden sm:right-20 md:h-[5em]"
      src="@/assets/small-cloud-1.png" alt="small clouds" />
    <img class="absolute top-2/3 left-0 z-10 h-[4em] overflow-hidden sm:top-1/2 sm:-left-20 md:h-[5em]"
      src="@/assets/small-cloud-2.png" alt="small  clouds" />

    <!-- leaderboards table -->
    <div v-if="!isLeaderboardPaused" class="mx-auto mt-6 flex justify-center sm:max-w-4xl lg:max-w-5xl">
      <div class="w-full rounded-[20px] border-[3px] border-[#FFC125]/70 shadow-md" style="
          background: linear-gradient(to top, #ffffff10, #d9d9d905);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
        ">
        <div class="overflow-x-auto rounded-[18px]">
          <!-- compact on mobile, airy on desktop -->
          <table
            class="z-20 min-w-[500px] w-full border-separate border-spacing-y-1 lg:border-spacing-y-3 xl:border-spacing-y-4">
            <thead>
              <tr>
                <th class="py-1 px-2 lg:py-3 lg:px-4 text-right font-inter text-xs lg:text-sm font-bold text-white">
                  RANK
                </th>
                <th class="py-1 px-2 lg:py-3 lg:px-4 text-center font-inter text-xs lg:text-sm font-bold text-white">
                  PRIZE
                </th>
                <th class="py-1 px-2 lg:py-3 lg:px-4 text-center font-inter text-xs lg:text-sm font-bold text-white">
                  USER
                </th>
                <th class="py-1 px-2 lg:py-3 lg:px-4 text-center font-inter text-xs lg:text-sm font-bold text-white">
                  WAGERED
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(item, index) in activeTopRest" :key="index"
                class="font-inter text-[16px] sm:text-xs lg:text-sm text-white">
                <!-- Rank -->
                <td class="text-white px-2 py-1 lg:px-4 lg:py-2.5 text-right">
                  {{ getOrdinalSuffix(index + 4) }}
                </td>

                <!-- Prize -->
                <td class="text-white px-2 py-1 lg:px-4 lg:py-2.5 text-center font-semibold">
                  <span class="text-[#FFC125]">{{ getRewardValue(index + 3) }}</span>
                  <img v-if="currentLeaderboard === 'raingg'" src="@/assets/raingg-coin.png" alt="raingg"
                    class="inline-block h-4 sm:h-5 w-4 sm:w-5 ml-1 align-[-2px]" />
                </td>

                <!-- User -->
                <td class="text-white px-2 py-1 lg:px-4 lg:py-2.5 text-center truncate max-w-[70px]">
                  {{ getMaskstring(item.username || "") }}
                </td>

                <!-- Wagered -->
                <td class="text-white px-2 py-1 lg:px-4 lg:py-2.5 text-center">
                  <span class="inline-flex items-center justify-center gap-1 sm:gap-1.5 leading-none tabular-nums">
                    <span class="text-[#FFC125] font-semibold">
                      {{ getCurrencyFormatted(item.wagered) }}
                    </span>
                    <img v-if="currentLeaderboard === 'raingg'" src="@/assets/raingg-coin.png" alt="raingg"
                      class="h-4 w-4 sm:h-5 sm:w-5" />
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="isLeaderboardPaused"
      class="z-30 text-white text-center mt-10 sm:mt-0 sm:text-6xl text-4xl font-bold py-13 sm:py-10">
      Leaderboard Paused
    </div>

    <!-- switch animation instruction for mobile screen -->
    <div v-if="!isLeaderboardPaused"
      class="flex md:hidden justify-center w-full max-w-7xl items-center space-x-4 text-white font-bold mt-[20px]">
      <!-- Background Container -->
      <div class="relative w-16 h-8 rounded-full bg-[rgba(255,255,255,0.05)] overflow-hidden">
        <!-- Animated Circle -->
        <div
          class="absolute top-1/2 left-0 transform -translate-y-1/2 w-4 h-4 rounded-full bg-white animate-scroll-dot">
        </div>
      </div>

      <span class="text-white">Scroll To Right</span>
    </div>

    <img class="absolute left-0 bottom-80 w-full" src="@/assets/leaderboard-table-bg.png" alt="translucent clouds" />

    <!-- overlay -->

    <img class="absolute bottom-12 -right-32 z-10 h-[7.5em] overflow-hidden md:-right-72 md:h-[18.75em]"
      src="@/assets/leaderboard-cloud-right.png" alt="right clouds" />

    <img class="absolute bottom-12 -left-32 z-10 h-[6.25em] overflow-hidden md:-left-72 md:h-[15.625em]"
      src="@/assets/leaderboard-cloud-left.png" alt="left clouds" />

    <img class="absolute left-0 -bottom-4 w-full" src="@/assets/leaderboard-white-curve.png" alt="translucent clouds" />

    <ChallengesSection />
  </section>
</template>

<script>
import axios from "axios";
import moment from "moment-timezone";
import ChallengesSection from "@/sections/ChallengesSection.vue";

export default {
  components: {
    ChallengesSection,
  },
  data() {
    return {
      selectedTab: "current",
      leaderboardData: {},
      leaderboardPrevData: {},
      top3: [],
      topRest: [],
      top3Prev: [],
      topRestPrev: [],
      topForMobile: [],
      timer: "0d 0h 0m 0s",
      timeSegments: [
        { label: "DAYS" },
        { label: "HRS" },
        { label: "MIN" },
        { label: "SEC" },
      ],
      currentLeaderboard: "roobet",
      previousLeaderboard: false,
      startDate: null,
      endDate: null,
      notFormattedEndDate: null,
      timerInterval: null,
      loading: false,
    };
  },
  computed: {
    activeTop3() {
      return this.selectedTab === "previous" ? this.top3Prev : this.top3;
    },
    activeTopRest() {
      return this.selectedTab === "previous" ? this.topRestPrev : this.topRest;
    },
    isLeaderboardPaused() {
      const leaderboardKey = `VUE_APP_PAUSE_LEADERBOARD_${this.currentLeaderboard.toUpperCase()}`;
      const pauseValue = process.env[leaderboardKey];

      if (pauseValue !== undefined) {
        return pauseValue === "true";
      }

      return !["roobet", "packdraw"].includes(this.currentLeaderboard);
    },
    showPreviousLeaderboard() {
      if (this.isLeaderboardPaused) return false;
      const leaderboardKey = `VUE_APP_SHOW_PREVIOUS_LEADERBOARD_${this.currentLeaderboard.toUpperCase()}`;
      return process.env[leaderboardKey] === "true";
    },
    leaderboardLogo() {
      if (this.currentLeaderboard === 'roobet') {
        return require('@/assets/5k_gold.avif');
      }
      if (this.currentLeaderboard === 'packdraw') {
        return require('@/assets/5k_gold.avif');
      }
      return require('@/assets/leaderboard-logo.png');
    },
  },
  watch: {
    selectedTab() {
      this.startTimer();
    },
    previousLeaderboard() {
      this.changeLeaderboard();
      this.startTimer();
    },
    currentLeaderboard() {
      this.selectedTab = "current";
      this.fetchLeaderboard();
    },
  },
  methods: {
    getPackdrawCycleDates() {
      const baseStart = moment.utc("2026-09-06T00:00:00Z");
      const cycleDays = 31;
      const now = moment.utc();

      if (now.isBefore(baseStart)) {
        const end = baseStart.clone().add(cycleDays, "days");
        return {
          startDate: baseStart.format("MMMM D, YYYY"),
          endDate: end.clone().subtract(1, "second").format("MMMM D, YYYY"),
          notFormattedEndDate: end.toISOString(),
          targetEndUtc: end,
        };
      }

      const diffMs = now.diff(baseStart);
      const cycleMs = cycleDays * 24 * 60 * 60 * 1000;
      const cycleIndex = Math.floor(diffMs / cycleMs);

      const currentCycleStart = baseStart.clone().add(cycleIndex * cycleDays, "days");
      const currentCycleEnd = baseStart.clone().add((cycleIndex + 1) * cycleDays, "days");

      return {
        startDate: currentCycleStart.format("MMMM D, YYYY"),
        endDate: currentCycleEnd.clone().subtract(1, "second").format("MMMM D, YYYY"),
        notFormattedEndDate: currentCycleEnd.toISOString(),
        targetEndUtc: currentCycleEnd,
      };
    },

    async fetchLeaderboard() {
      if (this.isLeaderboardPaused) {
        this.loading = false;
        return;
      }

      this.loading = true;

      // Clear old data to prevent displaying stale results
      this.leaderboardData = {};
      this.leaderboardPrevData = {};
      this.top3 = [];
      this.topRest = [];
      this.top3Prev = [];
      this.topRestPrev = [];
      this.topForMobile = [];

      const baseUrl = process.env.VUE_APP_BASE_API;
      const baseUrlDynamic = process.env.VUE_APP_BASE_API_DYNAMIC;
      const apiKey = process.env.VUE_APP_X_API_KEY;
      const apiKeyDynamic = process.env.VUE_APP_X_API_KEY_DYNAMIC;
      const secretKey = process.env.VUE_APP_X_SECRET_KEY;

      try {
        let currentRes;
        let previousRes;

        const isPackdraw = this.currentLeaderboard === "packdraw";
        const isRoobet = this.currentLeaderboard === "roobet";
        const isRainGG = this.currentLeaderboard === "raingg";

        // Fetch current leaderboard
        if (isPackdraw) {
          currentRes = await axios.get(
            `${baseUrlDynamic}/api/leaderboard/${this.currentLeaderboard}`,
            {
              headers: {
                "x-api-key": apiKeyDynamic,
                "x-secret-key": secretKey,
              },
            }
          );
        } else if (isRoobet) {
          currentRes = await axios.get(
            `${baseUrl}/api/${this.currentLeaderboard}/leaderboard`,
            {
              headers: {
                "x-api-key": apiKey,
              },
            }
          );
        } else if (isRainGG) {
          currentRes = await axios.get(
            `${baseUrlDynamic}/api/leaderboard/${this.currentLeaderboard}`,
            {
              headers: {
                "x-api-key": apiKeyDynamic,
                "x-secret-key": secretKey,
              },
            }
          );
        }

        this.leaderboardData = currentRes.data || {};

        if (this.leaderboardData.startDate && this.leaderboardData.endDate) {
          this.startDate = moment.utc(this.leaderboardData.startDate).format("MMMM D, YYYY");
          this.endDate = moment.utc(this.leaderboardData.endDate).format("MMMM D, YYYY");
          this.notFormattedEndDate = this.leaderboardData.endDate;
        } else if (isPackdraw) {
          const cycle = this.getPackdrawCycleDates();
          this.startDate = cycle.startDate;
          this.endDate = cycle.endDate;
          this.notFormattedEndDate = cycle.notFormattedEndDate;
        }

        // Fetch previous leaderboard
        if (isPackdraw) {
          previousRes = await axios.get(
            `${baseUrlDynamic}/api/leaderboard/${this.currentLeaderboard}?previous=true`,
            {
              headers: {
                "x-api-key": apiKeyDynamic,
                "x-secret-key": secretKey,
              },
            }
          );
        } else if (isRoobet) {
          previousRes = await axios.get(
            `${baseUrl}/api/${this.currentLeaderboard}/leaderboard?previous=true`,
            {
              headers: {
                "x-api-key": apiKey,
              },
            }
          );
        } else if (isRainGG) {
          previousRes = await axios.get(
            `${baseUrlDynamic}/api/leaderboard/${this.currentLeaderboard}?previous=true`,
            {
              headers: {
                "x-api-key": apiKeyDynamic,
                "x-secret-key": secretKey,
              },
            }
          );
        }

        this.leaderboardPrevData = previousRes.data || {};

        // Normalize both data sets
        this.leaderboardData = this.normalizeLeaderboardData(this.leaderboardData);
        this.leaderboardPrevData = this.normalizeLeaderboardData(
          this.leaderboardPrevData
        );

        this.changeLeaderboard();
        this.startTimer();
      } catch (error) {
        console.error("Error fetching leaderboard data:", error);
      } finally {
        this.loading = false;
      }
    },

    normalizeLeaderboardData(data) {
      if (!data?.leaderboard) return { leaderboard: [] };

      return {
        startDate: data.startDate,
        endDate: data.endDate,
        leaderboard: data.leaderboard.map((entry) => ({
          username: this.getUsername(entry),
          wagered: this.getWageredAmount(entry),
        })),
      };
    },

    toggleLeaderboard(leaderboardType) {
      if (this.currentLeaderboard !== leaderboardType) {
        this.currentLeaderboard = leaderboardType;
        this.selectedTab = "current";
        this.fetchLeaderboard();
      }
    },

    changeLeaderboard() {
      const currentData = this.leaderboardData.leaderboard || [];
      const previousData = this.leaderboardPrevData.leaderboard || [];

      // Top 3 and rest from current leaderboard
      this.top3 = currentData.slice(0, 3);
      this.topRest = currentData.slice(3, 15);
      this.topForMobile = currentData.slice(0, 15);

      // Top 3 and rest from previous leaderboard
      this.top3Prev = previousData.slice(0, 3);
      this.topRestPrev = previousData.slice(3, 15);

      if (this.leaderboardData.startDate && this.leaderboardData.endDate) {
        this.startDate = moment.utc(this.leaderboardData.startDate).format("MMMM D, YYYY");
        this.endDate = moment.utc(this.leaderboardData.endDate).format("MMMM D, YYYY");
        this.notFormattedEndDate = this.leaderboardData.endDate;
      } else if (this.currentLeaderboard === "packdraw") {
        const cycle = this.getPackdrawCycleDates();
        this.startDate = cycle.startDate;
        this.endDate = cycle.endDate;
        this.notFormattedEndDate = cycle.notFormattedEndDate;
      }

      this.startTimer();
    },

    getUsername(entry) {
      if (this.currentLeaderboard === "roobet") {
        return entry.username || "";
      } else if (this.currentLeaderboard === "packdraw") {
        return entry.username || "";
      } else if (this.currentLeaderboard === "raingg") {
        return entry.username || "";
      }
      return "";
    },

    getWageredAmount(entry) {
      if (!entry) return 0;
      switch (this.currentLeaderboard) {
        case "roobet":
          return entry.weightedWagered !== undefined ? entry.weightedWagered : (entry.wagered || entry.wagerAmount || 0);
        case "packdraw":
          return entry.wagerAmount !== undefined ? entry.wagerAmount : (entry.wagered || entry.weightedWagered || 0);
        case "raingg":
          return entry.wagered !== undefined ? entry.wagered : (entry.wagerAmount || entry.weightedWagered || 0);
        default:
          return 0;
      }
    },

    getOrdinalSuffix(n) {
      const s = ["th", "st", "nd", "rd"],
        v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },

    getMaskstring(string) {
      if (string) {
        const firstHalf = Math.ceil(Number(string.length / 2));
        const secondHalf = string.length - firstHalf;
        return string.slice(0, firstHalf) + "*".repeat(secondHalf);
      }
      return string;
    },

    getCurrencyFormatted(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return "";

      const formatted = num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      if (this.currentLeaderboard === "raingg") {
        return `${formatted}`;
      }
      return `$${formatted}`;
    },

    getRewardValue(rank, options) {
      const isPrevious = (options && options.previous !== undefined)
        ? options.previous
        : (this.selectedTab === "previous");

      const rewardsKey = `VUE_APP_${this.currentLeaderboard.toUpperCase()}_${
        isPrevious ? "PREVIOUS" : "CURRENT"
      }_LEADERBOARD_REWARDS`;

      let raw = process.env[rewardsKey] || "[]";
      let prizes = [];
      try {
        prizes = JSON.parse(raw);
      } catch {
        prizes = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (!Array.isArray(prizes)) prizes = [];

      if (rank >= prizes.length) return "$";

      const valueNum = Number(prizes[rank]) || 0;
      return this.getCurrencyFormatted(valueNum);
    },

    startTimer() {
      if (!this.notFormattedEndDate && this.currentLeaderboard === "packdraw") {
        const cycle = this.getPackdrawCycleDates();
        this.startDate = cycle.startDate;
        this.endDate = cycle.endDate;
        this.notFormattedEndDate = cycle.notFormattedEndDate;
      }

      if (!this.notFormattedEndDate) return;

      clearInterval(this.timerInterval);

      const updateTimer = () => {
        let nowUtc = moment.utc();
        let targetDate = moment.utc(this.notFormattedEndDate);

        // Auto-transition to next 31-day cycle when end date is reached
        if (nowUtc.isSameOrAfter(targetDate)) {
          if (this.currentLeaderboard === "packdraw") {
            const cycle = this.getPackdrawCycleDates();
            this.startDate = cycle.startDate;
            this.endDate = cycle.endDate;
            this.notFormattedEndDate = cycle.notFormattedEndDate;
            targetDate = moment.utc(this.notFormattedEndDate);
          } else {
            targetDate.add(31, "days");
          }
        }

        const distanceMs = targetDate.diff(nowUtc);
        const duration = moment.duration(distanceMs);

        const dNum = Math.floor(duration.asDays());
        const hNum = duration.hours();
        const mNum = duration.minutes();
        const sNum = duration.seconds();

        const dStr = String(dNum);
        const hStr = String(hNum);
        const mStr = String(mNum);
        const sStr = String(sNum);

        this.timer = `${dStr}d ${hStr}h ${mStr}m ${sStr}s`;

        const values = [dStr, hStr, mStr, sStr];
        this.timeSegments.forEach((segment, index) => {
          segment.value = values[index] || "0";
        });
      };

      updateTimer();
      this.timerInterval = setInterval(updateTimer, 1000);
    },
  },
  mounted() {
    this.fetchLeaderboard();
  },
  beforeUnmount() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  },
};
</script>
